import type * as React from "react";

import { ClockIcon } from "@lucide/icons";

import { fromParts } from "../dateAdapter";
import { tick } from "../lib";
import { invoke, isNil } from "../lib/utils";
import type { BasePickerOnChangeData } from "../pickers/BasePicker";
import HourPicker from "../pickers/timePicker/HourPicker";
import MinutePicker from "../pickers/timePicker/MinutePicker";
import InputView from "../views/InputView";
import BaseInput, {
  type BaseInputProps,
  type BaseInputState,
  type MultimodeProps,
  type TimeRelatedProps,
} from "./BaseInput";
import { TIME_FORMAT, buildValue, parseValue } from "./parse";

function getNextMode(currentMode: "hour" | "minute"): "hour" | "minute" {
  if (currentMode === "hour") {
    return "minute";
  }

  return "hour";
}

type CalendarMode = "hour" | "minute";

export type TimeInputProps = BaseInputProps & MultimodeProps & TimeRelatedProps;

export type TimeInputOnChangeData = TimeInputProps;

interface TimeInputState extends BaseInputState {
  mode: CalendarMode;
}

class TimeInput extends BaseInput<TimeInputProps, TimeInputState> {
  /**
   * Component responsibility:
   *  - parse time input string
   *  - switch between modes ['hour', 'minute']
   *  - handle HourPicker/MinutePicker change (format { hour: number, minute: number } into output time string)
   */
  public static readonly defaultProps = {
    ...BaseInput.defaultProps,
    icon: ClockIcon,
    timeFormat: "24",
    disableMinute: false,
  };

  constructor(props: TimeInputProps) {
    super(props);
    this.state = { mode: "hour", popupIsClosed: true };
  }

  public render() {
    const { value, timeFormat, closable, disableMinute, ...rest } = this.props;

    return (
      <InputView
        popupIsClosed={this.state.popupIsClosed}
        onMount={this.onInputViewMount}
        closePopup={this.closePopup}
        openPopup={this.openPopup}
        {...rest}
        value={value}
        renderPicker={() => this.getPicker()}
      />
    );
  }

  private handleSelect = (
    e: React.SyntheticEvent<HTMLElement> | null,
    { value }: BasePickerOnChangeData,
  ) => {
    tick(this.handleSelectUndelayed, e, { value });
  };

  private handleSelectUndelayed = (
    e: React.SyntheticEvent<HTMLElement> | null,
    { value }: BasePickerOnChangeData,
  ) => {
    const { hour, minute } = value;
    const { timeFormat, disableMinute } = this.props;
    const normalizedMinute = this.state.mode === "hour" ? 0 : minute;

    let outputTimeString = "";
    if (this.state.mode === "hour" && !isNil(hour)) {
      outputTimeString = fromParts({ hour, minute: normalizedMinute }).format(
        TIME_FORMAT[timeFormat!],
      );
    } else if (!isNil(hour) && !isNil(normalizedMinute)) {
      outputTimeString = fromParts({ hour, minute: normalizedMinute }).format(
        TIME_FORMAT[timeFormat!],
      );
    }
    invoke(this.props, "onChange", e, {
      ...this.props,
      value: outputTimeString,
    });
    if (
      this.props.closable &&
      (this.state.mode === "minute" || this.props.disableMinute)
    ) {
      this.closePopup();
    }
    if (!disableMinute) {
      this.switchToNextMode();
    }
  };

  private switchToNextMode = () => {
    this.setState(({ mode }) => {
      return { mode: getNextMode(mode) };
    }, this.onModeSwitch);
  };

  private getPicker() {
    const {
      value,
      timeFormat,
      inline,
      localization,
      tabIndex,
      pickerStyle,
      pickerWidth,
    } = this.props;
    const currentValue = parseValue(
      value,
      TIME_FORMAT[timeFormat!],
      localization,
    );
    const pickerProps = {
      inline,
      onCalendarViewMount: this.onCalendarViewMount,
      isPickerInFocus: this.isPickerInFocus,
      isTriggerInFocus: this.isTriggerInFocus,
      hasHeader: false,
      pickerWidth,
      pickerStyle,
      onHeaderClick: () => undefined,
      closePopup: this.closePopup,
      initializeWith: buildValue(
        currentValue,
        null,
        localization,
        TIME_FORMAT[timeFormat!],
      ),
      value: buildValue(
        currentValue,
        null,
        TIME_FORMAT[timeFormat!],
        localization,
        null,
      ),
      onChange: this.handleSelect,
      timeFormat,
      tabIndex,
      localization,
    };
    if (this.state.mode === "hour") {
      return <HourPicker {...(pickerProps as any)} />;
    }

    return <MinutePicker {...(pickerProps as any)} />;
  }
}

export default TimeInput;
